import React, { useState, useEffect } from 'react';
import apiClient, { getCurrentAppVersion } from '../utils/apiClient';

const VersionConfigManager = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editConfig, setEditConfig] = useState({
    minSupportedVersion: '',
    currentVersion: '',
    enabled: true
  });

  useEffect(() => {
    fetchVersionConfig();
  }, []);

  const fetchVersionConfig = async () => {
    try {
      const response = await apiClient.get('/api/admin/version-config');
      setConfig(response.data.config);
      setEditConfig({
        minSupportedVersion: response.data.config.MIN_SUPPORTED_VERSION,
        currentVersion: response.data.config.CURRENT_VERSION,
        enabled: response.data.config.ENABLED
      });
      setError('');
    } catch (error) {
      console.error('Failed to fetch version config:', error);
      setError('Failed to load version configuration. You may not have admin permissions.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await apiClient.put('/api/admin/version-config', editConfig);
      setConfig(response.data.config);
      setSuccess('Version configuration updated successfully!');
      
      // Show warning if user set version checking to be more restrictive
      const currentAppVersion = getCurrentAppVersion();
      if (editConfig.enabled && isVersionOutdated(currentAppVersion, editConfig.minSupportedVersion)) {
        setError(`Warning: Current app version (${currentAppVersion}) is below the minimum you just set (${editConfig.minSupportedVersion}). Users with this version will be forced to update.`);
      }
    } catch (error) {
      console.error('Failed to update version config:', error);
      setError(error.response?.data?.error || 'Failed to update version configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditConfig(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
    setSuccess('');
  };

  const testVersionCheck = () => {
    const testVersion = prompt('Enter a test version to check against current settings (e.g., "1.9.0"):');
    if (!testVersion) return;

    const wouldBeBlocked = isVersionOutdated(testVersion, editConfig.minSupportedVersion);
    const message = wouldBeBlocked 
      ? `❌ Version ${testVersion} would be BLOCKED (below minimum ${editConfig.minSupportedVersion})`
      : `✅ Version ${testVersion} would be ALLOWED (meets minimum ${editConfig.minSupportedVersion})`;
    
    alert(message);
  };

  // Version comparison utility
  function isVersionOutdated(currentVersion, minVersion) {
    if (!currentVersion || !minVersion) return false;
    
    const current = parseVersion(currentVersion);
    const minimum = parseVersion(minVersion);
    
    if (current.major < minimum.major) return true;
    if (current.major > minimum.major) return false;
    
    if (current.minor < minimum.minor) return true;
    if (current.minor > minimum.minor) return false;
    
    return current.patch < minimum.patch;
  }

  function parseVersion(version) {
    const [major, minor, patch] = version.replace(/[^0-9.]/g, '').split('.').map(Number);
    return { major: major || 0, minor: minor || 0, patch: patch || 0 };
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div>Loading version configuration...</div>
      </div>
    );
  }

  if (!config) {
    return (
      <div style={{ padding: '20px' }}>
        <div style={{ color: '#d32f2f', marginBottom: '16px' }}>
          {error || 'Unable to load version configuration'}
        </div>
      </div>
    );
  }

  return (
    <div style={{
      border: '1px solid #ddd',
      borderRadius: '8px',
      padding: '20px',
      margin: '16px 0',
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ marginTop: 0, marginBottom: '16px', fontSize: '18px' }}>
        📋 Version Control Configuration
      </h3>

      {/* Current Status */}
      <div style={{
        backgroundColor: config.ENABLED ? '#e8f5e8' : '#fff3cd',
        border: `1px solid ${config.ENABLED ? '#c3e6c3' : '#ffeaa7'}`,
        padding: '12px',
        borderRadius: '4px',
        marginBottom: '20px'
      }}>
        <strong>Current Status:</strong>
        <div style={{ marginTop: '8px', fontSize: '14px' }}>
          <div>✅ Version checking: {config.ENABLED ? 'Enabled' : 'Disabled'}</div>
          <div>📱 Current app version: {getCurrentAppVersion()}</div>
          <div>🚫 Minimum required: {config.MIN_SUPPORTED_VERSION}</div>
          <div>🎯 Latest version: {config.CURRENT_VERSION}</div>
        </div>
      </div>

      {/* Configuration Form */}
      <div style={{ marginBottom: '20px' }}>
        <h4 style={{ marginBottom: '12px' }}>Update Configuration</h4>
        
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: '500',
              fontSize: '14px'
            }}>
              Minimum Supported Version:
            </label>
            <input
              type="text"
              value={editConfig.minSupportedVersion}
              onChange={(e) => handleInputChange('minSupportedVersion', e.target.value)}
              placeholder="e.g., 2.0.0"
              style={{
                width: '200px',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Users with versions below this will be forced to update
            </div>
          </div>

          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: '500',
              fontSize: '14px'
            }}>
              Current Latest Version:
            </label>
            <input
              type="text"
              value={editConfig.currentVersion}
              onChange={(e) => handleInputChange('currentVersion', e.target.value)}
              placeholder="e.g., 2.1.0"
              style={{
                width: '200px',
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              Version users will be prompted to update to
            </div>
          </div>

          <div>
            <label style={{ 
              display: 'flex', 
              alignItems: 'center',
              fontSize: '14px',
              fontWeight: '500'
            }}>
              <input
                type="checkbox"
                checked={editConfig.enabled}
                onChange={(e) => handleInputChange('enabled', e.target.checked)}
                style={{ marginRight: '8px' }}
              />
              Enable Version Checking
            </label>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
              When disabled, all client versions will be accepted
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          border: '1px solid #ffcdd2'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          backgroundColor: '#e8f5e8',
          color: '#2e7d2e',
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px',
          border: '1px solid #c3e6c3'
        }}>
          {success}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <button
          onClick={handleSaveConfig}
          disabled={saving}
          style={{
            backgroundColor: saving ? '#ccc' : '#2196f3',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: saving ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>

        <button
          onClick={testVersionCheck}
          style={{
            backgroundColor: '#6c757d',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Test Version Check
        </button>

        <button
          onClick={fetchVersionConfig}
          style={{
            backgroundColor: 'white',
            color: '#6c757d',
            border: '1px solid #ccc',
            padding: '10px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          Refresh
        </button>
      </div>

      {/* Info */}
      <div style={{
        marginTop: '20px',
        padding: '12px',
        backgroundColor: '#e3f2fd',
        borderRadius: '4px',
        fontSize: '13px',
        color: '#1565c0'
      }}>
        <div style={{ fontWeight: '500', marginBottom: '4px' }}>💡 How it works:</div>
        <ul style={{ margin: '0', paddingLeft: '20px' }}>
          <li>All API requests include the frontend version in headers</li>
          <li>Server rejects requests from versions below the minimum</li>
          <li>Users get a blocking modal forcing them to update</li>
          <li>Public routes (login, registration) are not version-checked</li>
        </ul>
      </div>
    </div>
  );
};

export default VersionConfigManager; 